/**
 * The tenant's copilot (V1-E09-T04): the model it asks to draft agents, review calls and propose fixes, and the
 * embedder its reviews group calls with (without one, they are grouped by the words they share). An admin's to
 * change (`settings:write`).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState } from "../ui/states";

type Setting = Schemas["CopilotSetting"];
const MODEL_PROVIDERS = ["openai", "groq", "openai-compatible", "endpoint", "fake"] as const;
const EMBEDDER_PROVIDERS = ["openai", "openai-compatible", "endpoint", "fake"] as const;
const field = "w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm disabled:opacity-60";

interface Ref { provider: string; model: string; credential: string; endpoint: string }

const blank: Ref = { provider: "openai", model: "", credential: "", endpoint: "" };
const of = (r: { provider: string; model?: string | null; credential?: string | null; endpoint?: string | null } | null | undefined): Ref =>
  r ? { provider: r.provider, model: r.model ?? "", credential: r.credential ?? "", endpoint: r.endpoint ?? "" } : blank;
/** The reference as the API takes it: what the provider needs, nothing empty. */
function body(r: Ref) {
  return {
    provider: r.provider,
    ...(r.model && r.provider !== "fake" ? { model: r.model } : {}),
    ...(r.provider === "endpoint" ? { endpoint: r.endpoint } : {}),
    ...(r.credential && r.provider !== "endpoint" && r.provider !== "fake" ? { credential: r.credential } : {}),
  };
}

export function CopilotModel({ current }: { current: Setting | null | undefined }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const canChange = useCan("settings:write");
  const credentials = useApiQuery("credentials", (a) => a.GET("/v1/credentials"));
  const endpoints = useApiQuery("model-endpoints", (api) => api.GET("/v1/model-endpoints", { params: { query: { limit: 200 } } }));
  const [model, setModel] = useState<Ref>(of(current?.model));
  const [backup, setBackup] = useState(Boolean(current?.fallback));
  const [fallback, setFallback] = useState<Ref>(of(current?.fallback));
  const [grouping, setGrouping] = useState(Boolean(current?.embedder));
  const [embedder, setEmbedder] = useState<Ref>(of(current?.embedder));
  const save = useMutation({
    mutationFn: (value: Setting | null) => data(api.PATCH("/v1/tenant", { body: { copilot: value } })),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["tenant", tenant] }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({
      model: body(model), ...(backup ? { fallback: body(fallback) } : {}), ...(grouping ? { embedder: body(embedder) } : {}),
    } as Setting);
  };
  const names = (credentials.data?.data ?? []).map((c) => c.name);
  const endpointNames = (endpoints.data?.data ?? []).map((e) => e.name);
  return (
    <form onSubmit={submit} className="max-w-xl space-y-4">
      <p className="text-sm text-[var(--color-muted)]">{t("settings.copilotIntro")}</p>
      <fieldset disabled={!canChange || save.isPending} className="space-y-3">
        <legend className="text-sm font-semibold">{t("settings.copilotModel")}</legend>
        <RefFields value={model} onChange={setModel} providers={MODEL_PROVIDERS} credentials={names} endpoints={endpointNames} modelHint={t("settings.copilotModelHint")} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={backup} onChange={(e) => setBackup(e.target.checked)} className="size-4 accent-[var(--color-primary)]" />
          {t("settings.copilotFallback")}
        </label>
        <p className="text-xs text-[var(--color-muted)]">{t("settings.copilotFallbackHint")}</p>
        {backup && <RefFields value={fallback} onChange={setFallback} providers={MODEL_PROVIDERS} credentials={names} endpoints={endpointNames}
          modelHint={t("settings.copilotModelHint")} />}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={grouping} onChange={(e) => setGrouping(e.target.checked)} className="size-4 accent-[var(--color-primary)]" />
          {t("settings.copilotEmbedder")}
        </label>
        <p className="text-xs text-[var(--color-muted)]">{t("settings.copilotEmbedderHint")}</p>
        {grouping && <RefFields value={embedder} onChange={setEmbedder} providers={EMBEDDER_PROVIDERS} credentials={names} endpoints={endpointNames}
          modelHint={t("settings.copilotEmbedderModelHint")} />}
      </fieldset>
      {canChange && (
        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>{t("settings.copilotSave")}</Button>
          {current && <Button variant="secondary" onClick={() => save.mutate(null)} disabled={save.isPending}>{t("settings.copilotRemove")}</Button>}
        </div>
      )}
      {save.isSuccess && <p role="status" className="text-sm">{t("settings.saved")}</p>}
      {save.isError && <ErrorState error={save.error} />}
    </form>
  );
}

/** A choice among what exists (V1.2-UX-T09): typing a name that doesn't is no longer possible. A value set before that
 * no longer exists stays listed, so it can be seen and changed; with nothing to choose, a link to make one. */
function Picker({ id, label, value, names, onChange, empty, to, hash }: {
  id: string; label: string; value: string; names: string[]; onChange: (v: string) => void; empty: string; to: "/developers" | "/models"; hash?: string;
}) {
  const t = useT();
  const listed = value && !names.includes(value) ? [value, ...names] : names;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium">{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={`${field} font-mono`} required>
        <option value="">{t("settings.choose")}</option>
        {listed.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      {names.length === 0 && (
        <p className="text-xs text-[var(--color-muted)]">
          {empty} <Link to={to} hash={hash} className="text-[var(--color-primary)] underline">{t("settings.addOne")}</Link>
        </p>
      )}
    </div>
  );
}

function RefFields({ value, onChange, providers, credentials, endpoints, modelHint }: {
  value: Ref; onChange: (r: Ref) => void; providers: readonly string[]; credentials: string[]; endpoints: string[]; modelHint: string;
}) {
  const t = useT();
  const id = useId();
  const set = (k: keyof Ref) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1">
        <label htmlFor={`${id}-p`} className="block text-sm font-medium">{t("settings.copilotProvider")}</label>
        <select id={`${id}-p`} value={value.provider} onChange={set("provider")} className={field}>
          {providers.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      {value.provider === "endpoint" ? (
        <Picker id={`${id}-e`} label={t("settings.copilotEndpoint")} value={value.endpoint} names={endpoints}
          onChange={(v) => onChange({ ...value, endpoint: v })} empty={t("settings.noEndpoints")} to="/models" />
      ) : value.provider !== "fake" && (
        <Picker id={`${id}-c`} label={t("settings.copilotCredential")} value={value.credential} names={credentials}
          onChange={(v) => onChange({ ...value, credential: v })} empty={t("settings.noCredentials")} to="/developers" hash="credentials" />
      )}
      {value.provider !== "fake" && (
        <div className="space-y-1 sm:col-span-2">
          <label htmlFor={`${id}-m`} className="block text-sm font-medium">{t("settings.copilotModelName")}</label>
          <input id={`${id}-m`} value={value.model} onChange={set("model")} className={`${field} font-mono`} aria-describedby={`${id}-mh`} />
          <p id={`${id}-mh`} className="text-xs text-[var(--color-muted)]">{modelHint}</p>
        </div>
      )}
    </div>
  );
}
